import type { CallbackSettingsInput } from '@jlg-website/shared';
import { CONFIRM_LEAD_MS } from './constants';
import { DATE_FORMAT, dateTime } from './date-time';
import { officeDayBounds } from './office-day';

type GateQuery = {
  settings: CallbackSettingsInput;
  visitorTimeZone: string;
  now: Date;
};

/**
 * Whether the visitor reached the scheduler outside the office's day: before it opens, once the
 * last slot is too close to start to be booked, or on a day that takes no callbacks at all.
 *
 * Both edges are the first and the last slot shifted by `CONFIRM_LEAD_MS` — the same gate a
 * booking is confirmed with. A 09:30–19:30 office day (15-minute slots, so the last one starts at
 * 19:15) is open from 09:28 to 19:13, on whatever clock the visitor reads it: an hour behind the
 * office, that same day is theirs from 08:28 to 18:13.
 *
 * Read from the slot *start times* rather than their availability on purpose — a day whose slots
 * are all taken is still a working day, and the visitor belongs in the picker.
 */
export function isOutsideBusinessHours({ settings, visitorTimeZone, now }: GateQuery): boolean {
  const today = dateTime(now).tz(visitorTimeZone).format(DATE_FORMAT);
  const bounds = officeDayBounds({ date: today, settings, visitorTimeZone });

  if (!bounds) return true;

  return (
    now.getTime() < bounds.opensAt - CONFIRM_LEAD_MS ||
    now.getTime() > bounds.lastSlotStartsAt - CONFIRM_LEAD_MS
  );
}
